/**
 * The customer dashboard's design tokens (SF-217, increment 1).
 *
 * Transcribed once from the approved prototype's `prototype/styles/tokens.css` at 63f8fd35b
 * (docs/plans/20261013-sf217-ui-redesign/, sha256 3754833d75cff6f0...). From here on this file is
 * the source: change a value here, not in the prototype, and the contrast and discrimination tests
 * in ./__tests__ say whether the change still holds.
 *
 * Left out on purpose: the basemap's `--map-*` set, which arrives with the map's design
 * (increment 3), and the prototype's motion and stacking tokens, which MUI already provides.
 */

export type ThemeMode = 'light' | 'dark';

/** The nine category colours: a kind of item, a way in or a step, each with the ink drawn on it. */
export const CATEGORY_NAMES = ['purple', 'gold', 'green', 'blue', 'pink', 'sky', 'red', 'teal', 'grey'] as const;
export type CategoryName = (typeof CATEGORY_NAMES)[number];

export interface CategoryColour {
  fill: string;
  on: string;
}

export interface ColourTokens {
  paper: string;
  surface: string;
  sunken: string;
  ink: string;
  ink2: string;
  ink3: string;
  rule: string;
  ruleStrong: string;
  /** The one attention colour. Fills use `accent` with `onAccent` on top; text uses `accentInk`. */
  accent: string;
  accentInk: string;
  onAccent: string;
  accentWash: string;
  /** Map areas only: never text, never for anything that asks for action. */
  zoneHigh: string;
  zoneRestricted: string;
  /** One series is drawn in `seriesInk`; two to four take series 1 to 4 in order. */
  seriesInk: string;
  series: readonly [string, string, string, string];
  chartGrid: string;
  category: Record<CategoryName, CategoryColour>;
  /** States, for pictograms and fills at 3:1. Text takes the text-safe colours in ./statusText.ts. */
  status: {
    completed: string;
    running: string;
    late: string;
    waiting: string;
    failed: string;
  };
  scrim: string;
  focus: string;
}

const LIGHT: ColourTokens = {
  paper: '#f3f3f1',
  surface: '#ffffff',
  sunken: '#eaeae7',
  ink: '#121316',
  ink2: '#45484e',
  ink3: '#62666d',
  rule: '#d8d9d5',
  ruleStrong: '#868985',
  accent: '#ee5a1f',
  accentInk: '#b43f0e',
  onAccent: '#121316',
  accentWash: '#fdeee7',
  zoneHigh: '#a3463f',
  zoneRestricted: '#8c6a1a',
  seriesInk: '#121316',
  series: ['#2f6fb8', '#b08a14', '#8757b3', '#1a957f'],
  chartGrid: '#e4e5e1',
  category: {
    purple: { fill: '#8e128e', on: '#ffffff' },
    gold: { fill: '#8e791b', on: '#ffffff' },
    green: { fill: '#116b1a', on: '#ffffff' },
    blue: { fill: '#1949c5', on: '#ffffff' },
    pink: { fill: '#a75e8a', on: '#ffffff' },
    sky: { fill: '#3f7fb8', on: '#ffffff' },
    red: { fill: '#c0262d', on: '#ffffff' },
    teal: { fill: '#0b8e7a', on: '#ffffff' },
    grey: { fill: '#5f6670', on: '#ffffff' },
  },
  status: {
    completed: '#116b1a',
    running: '#1949c5',
    late: '#b43f0e',
    waiting: '#b43f0e',
    failed: '#c0262d',
  },
  scrim: 'rgb(18 19 22 / 0.32)',
  focus: '#b43f0e',
};

const DARK: ColourTokens = {
  paper: '#101113',
  surface: '#17181b',
  sunken: '#1f2125',
  ink: '#ececea',
  ink2: '#b9bbbf',
  ink3: '#9a9da3',
  rule: '#2c2e33',
  ruleStrong: '#6f7379',
  accent: '#ee5a1f',
  accentInk: '#ff8f61',
  onAccent: '#121316',
  accentWash: '#2e1a12',
  zoneHigh: '#d0766e',
  zoneRestricted: '#c79f45',
  seriesInk: '#ececea',
  series: ['#4f8bd6', '#b08a1c', '#9b74c9', '#2fa28d'],
  chartGrid: '#2c2e33',
  category: {
    purple: { fill: '#a372f5', on: '#121316' },
    gold: { fill: '#915b19', on: '#ffffff' },
    green: { fill: '#5da900', on: '#121316' },
    blue: { fill: '#0f62d3', on: '#ffffff' },
    pink: { fill: '#c2337c', on: '#ffffff' },
    sky: { fill: '#30a1bd', on: '#121316' },
    red: { fill: '#e5484d', on: '#121316' },
    teal: { fill: '#1fa58c', on: '#121316' },
    grey: { fill: '#8b929c', on: '#121316' },
  },
  status: {
    completed: '#5da900',
    running: '#0f62d3',
    late: '#ff8f61',
    waiting: '#ff8f61',
    failed: '#e5484d',
  },
  scrim: 'rgb(0 0 0 / 0.56)',
  focus: '#ff8f61',
};

export const colourTokens = (mode: ThemeMode): ColourTokens => (mode === 'dark' ? DARK : LIGHT);

export const FONT_SANS =
  '"Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
export const FONT_MONO =
  '"Geist Mono", ui-monospace, "SF Mono", SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

/** A working tool's scale, in rem: body 14px, data 13px, nothing below 12px. */
export const TEXT = {
  label: '0.75rem',
  data: '0.8125rem',
  body: '0.875rem',
  lead: '1rem',
  title: '1.25rem',
  display: '1.75rem',
} as const;
export const LEADING = { tight: 1.2, body: 1.45 } as const;

/*
 * Space: the prototype's 4px scale (4, 8, 12, 16, 24, 32, 48, 64) is MUI's own 8px `spacing(n)` in
 * half steps, so the theme keeps MUI's unit and every `p: 2` in the app keeps its size.
 */
export const RADIUS_PX = 2;
export const CONTROL_HEIGHT_PX = 32;
export const ROW_HEIGHT_PX = 36;
export const BAR_HEIGHT_PX = 48;
